import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  placeholder?: string;
  className?: string;
  initialPeriod?: 'AM' | 'PM';
  minuteInterval?: 15 | 30 | 'timeoff'; // 15 for 15-minute intervals, 30 for 30-minute intervals, 'timeoff' for [0, 15, 30]
  alignRight?: boolean; // If true, align dropdown to the right edge of the input
  dropdownDirection?: 'up' | 'down'; // Controls whether the dropdown opens upward or downward
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  placeholder = "--:-- --",
  className = "",
  initialPeriod,
  minuteInterval = 15,
  alignRight = false,
  dropdownDirection = 'down'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number>(12);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);
  const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>('AM');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties | null>(null);

  // Generate hours (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  
  // Generate minutes based on interval
  const minutes = useMemo(() => {
    if (minuteInterval === 30) return [0, 30];
    if (minuteInterval === 'timeoff') return [0, 15, 30];
    return [0, 15, 30, 45];
  }, [minuteInterval]);
  
  const periods: ('AM' | 'PM')[] = ['AM', 'PM'];

  // Parse current value
  useEffect(() => {
    if (value) {
      const [time, period] = value.split(' ');
      if (time) {
        const [hour, minute] = time.split(':').map(Number);
        if (hour && minute !== undefined) {
          setSelectedHour(hour);
          // Round minute to nearest valid interval
          let roundedMinute = minute;
          if (minuteInterval === 30) {
            roundedMinute = minute < 30 ? 0 : 30;
          } else if (minuteInterval === 'timeoff') {
            const allowed = [0, 15, 30];
            roundedMinute = allowed.reduce((best, cur) => (Math.abs(cur - minute) < Math.abs(best - minute) ? cur : best), allowed[0]);
          } else {
            // For 15-minute intervals (including timeoff), round to nearest
            roundedMinute = Math.round(minute / 15) * 15;
            if (roundedMinute === 60) roundedMinute = 0;
          }
          setSelectedMinute(roundedMinute);
          setSelectedPeriod(period as 'AM' | 'PM');
        }
      }
    } else if (initialPeriod) {
      // When there is no current value, ensure period defaults to provided initialPeriod
      setSelectedPeriod(initialPeriod);
    }
  }, [value, initialPeriod, minuteInterval]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current && rootRef.current.contains(target)) return;
      if (panelRef.current && panelRef.current.contains(target)) return;
      if (isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const updatePanelPosition = () => {
    if (typeof window === 'undefined') return;
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const panelEl = panelRef.current;
    const margin = 8;
    const panelWidth = panelEl?.offsetWidth ?? 256; // tailwind w-64
    const panelHeight = panelEl?.offsetHeight ?? 260;

    // horizontal position
    let left = alignRight ? rect.right - panelWidth : rect.left;
    left = Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin));

    // vertical position (with flip if needed)
    let top: number;
    if (dropdownDirection === 'up') {
      top = rect.top - panelHeight - margin;
      if (top < margin) top = rect.bottom + margin;
    } else {
      top = rect.bottom + margin;
      if (top + panelHeight > window.innerHeight - margin) top = rect.top - panelHeight - margin;
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - panelHeight - margin));

    setPanelStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 100000
    });
  };

  // Keep the panel positioned on open / scroll / resize
  useEffect(() => {
    if (!isOpen) return;
    updatePanelPosition();

    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('resize', onScrollOrResize);
    // capture scroll from any ancestor (modal bodies, etc.)
    window.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, alignRight, dropdownDirection, value, selectedHour, selectedMinute, selectedPeriod]);

  const handleTimeSelect = (hour: number, minute: number, period: 'AM' | 'PM') => {
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setSelectedPeriod(period);
    
    // Format time as HH:MM AM/PM
    const formattedHour = hour.toString().padStart(2, '0');
    const formattedMinute = minute.toString().padStart(2, '0');
    const timeString = `${formattedHour}:${formattedMinute} ${period}`;
    
    onChange(timeString);
    // Don't close dropdown - let user continue selecting other components
  };

  const displayValue = value || placeholder;
  return (
    <div className="relative" ref={rootRef}>
      <div
        ref={triggerRef}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer flex items-center justify-between ${
          className.includes('border-red-500') ? 'border-red-500' : 'border-gray-300'
        } ${className}`}
        onClick={() => {
          const nextOpen = !isOpen;
          if (nextOpen && !value && initialPeriod) {
            setSelectedPeriod(initialPeriod);
          }
          setIsOpen(nextOpen);
        }}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {displayValue}
        </span>
        <div className="flex items-center">
          <div className="w-2"></div>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      {isOpen && (
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            className="w-64 bg-white border border-gray-300 rounded-lg shadow-lg"
            style={panelStyle || { position: 'fixed', zIndex: 100000, top: 0, left: 0, visibility: 'hidden' }}
          >
            {/* Close button */}
            <div className="flex justify-between items-center p-2 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">Select Time</span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-1 rounded transition-colors"
                aria-label="Close time picker"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-3">
              <div className="grid grid-cols-3 gap-2">
                {/* Hours */}
                <div className="max-h-48 overflow-y-auto">
                  <div className="text-xs font-medium text-gray-500 mb-2 text-center">Hour</div>
                  {hours.map((hour) => (
                    <div
                      key={hour}
                      className={`px-2 py-1 text-center cursor-pointer rounded text-sm ${
                        selectedHour === hour ? 'bg-[#0B4778] text-white' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => handleTimeSelect(hour, selectedMinute, selectedPeriod)}
                    >
                      {hour.toString().padStart(2, '0')}
                    </div>
                  ))}
                </div>

                {/* Minutes */}
                <div className="max-h-48 overflow-y-auto">
                  <div className="text-xs font-medium text-gray-500 mb-2 text-center">Minute</div>
                  {minutes.map((minute) => (
                    <div
                      key={minute}
                      className={`px-2 py-1 text-center cursor-pointer rounded text-sm ${
                        selectedMinute === minute ? 'bg-[#0B4778] text-white' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => handleTimeSelect(selectedHour, minute, selectedPeriod)}
                    >
                      {minute.toString().padStart(2, '0')}
                    </div>
                  ))}
                </div>

                {/* AM/PM */}
                <div className="max-h-48 overflow-y-auto">
                  <div className="text-xs font-medium text-gray-500 mb-2 text-center">Period</div>
                  {periods.map((period) => (
                    <div
                      key={period}
                      className={`px-2 py-1 text-center cursor-pointer rounded text-sm ${
                        selectedPeriod === period ? 'bg-[#0B4778] text-white' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => handleTimeSelect(selectedHour, selectedMinute, period)}
                    >
                      {period}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      )}
    </div>
  );
};

export default TimePicker; 